import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Stack } from '@mui/material';

import styled from 'styled-components';

import { BlockedOfferingsFlag, FEATURE_FLAGS, useFeature } from '@arrived/feature-flags';
import { Offering } from '@arrived/models';

import { getAssetTypeFormattedMessage, PropertyTag } from '../../../PropertyTag';
import { PropertyDetailsHeaderName } from './PropertyDetailsHeaderName';
import { PropertyDetailsHouseInfo } from './PropertyDetailsHouseInfo';

import { useIsMobile } from '../../../hooks/useIsMobile';

interface PropertyDetailsHeaderProps {
  offering: Offering;
}

const StyledPropertyDetailsHeader = styled.div`
  z-index: 2;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-left: ${({ theme }) => theme.spacing(3)};
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .property-details-header {
      flex-direction: column;
      flex-basis: auto;
      flex-wrap: nowrap;
      align-items: stretch;
      justify-content: flex-start;
      padding: ${({ theme }) => theme.spacing(2)};
    }
  }
`;

export const StyledPropertyDetailsHeaderMobile = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(3, 3, 2)};
`;

export const PropertyDetailsHeader = ({ offering }: PropertyDetailsHeaderProps) => {
  const isMobile = useIsMobile();
  const blockedOfferingsFlag = useFeature<BlockedOfferingsFlag>(FEATURE_FLAGS.BLOCKED_OFFERINGS);
  const isOfferingBlocked = useMemo(
    () => blockedOfferingsFlag.value?.properties.includes(offering.shortName),
    [offering.shortName, blockedOfferingsFlag]
  );

  const { assetType, isFund, properties, name } = offering;
  const property = properties.length ? properties[0] : undefined;

  const assetTypeFormattedMessage = useMemo(() => getAssetTypeFormattedMessage(assetType), [assetType]);

  const PropertyTitleComponent = useCallback(
    () => (
      <Stack alignItems="center" direction="row" flexWrap="wrap" columnGap={1.5} rowGap={0.5}>
        <PropertyDetailsHeaderName name={name} />
        <PropertyTag label={<FormattedMessage {...assetTypeFormattedMessage} />} />
      </Stack>
    ),
    [assetTypeFormattedMessage, name]
  );

  return (
    <StyledPropertyDetailsHeader>
      {isMobile ? (
        <StyledPropertyDetailsHeaderMobile>
          <PropertyTitleComponent />
          {!isOfferingBlocked && !isFund ? <PropertyDetailsHouseInfo property={property} /> : null}
        </StyledPropertyDetailsHeaderMobile>
      ) : (
        <>
          <PropertyTitleComponent />
          {!isFund ? <PropertyDetailsHouseInfo property={property} /> : null}
        </>
      )}
    </StyledPropertyDetailsHeader>
  );
};
